import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  deleteJobQuery,
  deletePreviewQuery,
  launchDelete,
  type DeleteTargetType,
  type MembersMode,
} from './api';
import { DeleteFlowDialog } from './DeleteFlowDialog';
import type { DeletePhase } from './DeleteStatusBadge';

interface UseDeleteFlowArgs {
  readonly targetType: DeleteTargetType;
  readonly guid: string;
  /** Display name of the target — echoed in the copy and used as the type-to-confirm phrase. */
  readonly entityLabel: string;
  /**
   * Called once the delete job reaches COMPLETED. Do the query invalidation / navigation here — the
   * invalidated list refetch IS the post-job existence check (the card disappears when the target is
   * gone). Wrap in `useCallback` so the terminal-status effect does not refire.
   */
  readonly onDeleted: () => void;
}

interface DeleteFlow {
  /** Open the confirm flow — wire to the trash button's onClick. */
  readonly start: () => void;
  /** The confirm/blocked/wizard dialog (or `null` when idle). Render it unconditionally. */
  readonly overlay: ReactElement | null;
  /** Launch-pending or live job status — feed to `DeleteStatusBadge` for the in-flight affordance. */
  readonly phase: DeletePhase;
  /** True while launching or while the job is non-terminal — disable the trash button. */
  readonly isBusy: boolean;
}

const TERMINAL_NON_COMPLETED: ReadonlySet<string> = new Set(['FAILED', 'STOPPED', 'ABANDONED']);

/**
 * Drives the full delete UX for one target (pedigree / family / patient): fetch the dry-run preview,
 * pick the right confirmation (plain confirm / wizard / blocked notice), launch the delete as a job,
 * poll it, and fire {@link UseDeleteFlowArgs.onDeleted} on terminal success. The surface renders
 * `overlay` (the dialog) plus a {@link DeleteStatusBadge} driven by `phase`, and disables its trash
 * button while `isBusy`.
 */
export function useDeleteFlow({
  targetType,
  guid,
  entityLabel,
  onDeleted,
}: UseDeleteFlowArgs): DeleteFlow {
  const [isConfirming, setIsConfirming] = useState(false);
  const [membersMode, setMembersMode] = useState<MembersMode>('UNLINK');
  const [jobId, setJobId] = useState<string | null>(null);

  const previewMembers = targetType === 'FAMILY' ? membersMode : undefined;
  const previewQuery = useQuery({
    ...deletePreviewQuery(targetType, guid, previewMembers),
    enabled: isConfirming && guid.length > 0,
  });

  const jobQuery = useQuery(deleteJobQuery(jobId));
  const jobStatus = jobQuery.data?.status;

  const launch = useMutation({
    mutationFn: () => launchDelete(targetType, guid, previewMembers),
    onSuccess: (job) => {
      setJobId(job.jobId);
      setIsConfirming(false);
    },
  });

  // Terminal success: fire the caller's invalidation/navigation, then drop the job id (which changes
  // the query key so this effect cannot refire the same completion).
  useEffect(() => {
    if (jobStatus === 'COMPLETED') {
      onDeleted();
      setJobId(null);
    }
  }, [jobStatus, onDeleted]);

  const start = (): void => {
    setJobId(null);
    setMembersMode('UNLINK');
    setIsConfirming(true);
  };

  const phase: DeletePhase = launch.isPending
    ? 'pending'
    : jobId !== null
      ? (jobStatus ?? 'pending')
      : null;

  const isBusy =
    phase !== null && (phase === 'pending' || !TERMINAL_NON_COMPLETED.has(phase) && phase !== 'COMPLETED');

  const previewStatus: 'pending' | 'error' | 'ready' = previewQuery.isPending
    ? 'pending'
    : previewQuery.isError
      ? 'error'
      : 'ready';

  const overlay = isConfirming ? (
    <DeleteFlowDialog
      targetType={targetType}
      entityLabel={entityLabel}
      previewStatus={previewStatus}
      impact={previewQuery.data}
      membersMode={membersMode}
      onMembersModeChange={setMembersMode}
      isPreviewFetching={previewQuery.isFetching && !previewQuery.isPending}
      launching={launch.isPending}
      onCancel={() => {
        setIsConfirming(false);
      }}
      onConfirm={() => {
        launch.mutate();
      }}
    />
  ) : null;

  return { start, overlay, phase, isBusy };
}
